/**
 * @module CopyButton
 * @description Small inline copy-to-clipboard icon button. Copies `value` to the
 * clipboard and briefly swaps the clipboard icon for a check. Used next to the
 * version strings (Elera Client Base Version, Majestic OS Version) in the Fleet
 * Manager panels so a tester can grab the value without selecting text.
 */
import React, { useState, useRef, useEffect } from 'react';
import { ClipboardCopy, Check, X } from 'lucide-react';

function CopyButton({ value, label = 'Copy' }) {
  /** 'idle' | 'copied' | 'error' — drives the icon and the title/aria-label. */
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);

  /** Clear the pending reset timer if the panel closes mid-flash (no stray setState). */
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const flash = (next) => {
    setStatus(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStatus('idle'), 1500);
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      flash('copied');
    } catch {
      /**
       * Clipboard write can fail outside a secure context (http on a non-localhost
       * origin). Surface it rather than failing silently: for the module-table copy
       * the payload is a generated markdown table that exists nowhere on screen to
       * select as a fallback, so a silent failure would leave the user with nothing.
       */
      flash('error');
    }
  };

  const title = status === 'copied' ? 'Copied!' : status === 'error' ? 'Copy failed' : label;
  const ariaLabel = status === 'copied' ? 'Copied to clipboard' : status === 'error' ? 'Copy failed' : label;

  return (
    <button
      type="button"
      className="fleet-copy-btn"
      onClick={handleCopy}
      title={title}
      aria-label={ariaLabel}
    >
      {status === 'copied' ? <Check size={13} /> : status === 'error' ? <X size={13} /> : <ClipboardCopy size={13} />}
    </button>
  );
}

export default CopyButton;
